import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/services/api';

interface WhiteLabelConfig {
  displayName: string;
  slogan: string;
  logoUrl: string;
  darkLogoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  surfaceColor: string;
  accentColor: string;
  textColor: string;
  textSecondaryColor: string;
  borderColor: string;
  errorColor: string;
  successColor: string;
  onSecondaryColor: string;
  secondaryHoverColor: string;
  transitionDuration: string;
  easing: string;
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '201,151,79';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

const DEFAULTS: WhiteLabelConfig = {
  displayName: 'NextPay',
  slogan: 'Leitor de eBooks',
  logoUrl: '',
  primaryColor: '#9f7032',
  secondaryColor: '#c9974f',
  backgroundColor: '#060d20',
  surfaceColor: '#0d162f',
  accentColor: '#1c2a4d',
  textColor: '#ffffff',
  textSecondaryColor: '#94a3b8',
  borderColor: '#1c2a4d',
  errorColor: '#ef4444',
  successColor: '#10b981',
  onSecondaryColor: '#191919',
  secondaryHoverColor: '#b98744',
  transitionDuration: '0.3s',
  easing: 'ease-in-out',
};

export const useWhiteLabelStore = defineStore('whiteLabel', () => {
  const isLoaded = ref(false);
  const config = ref<WhiteLabelConfig>({ ...DEFAULTS });

  function applyConfig() {
    const root = document.documentElement;
    const c = config.value;

    root.style.setProperty('--primary-color', c.primaryColor);
    root.style.setProperty('--secondary-color', c.secondaryColor);
    root.style.setProperty('--secondary-color-rgb', hexToRgb(c.secondaryColor));
    root.style.setProperty('--primary-color-rgb', hexToRgb(c.primaryColor));
    root.style.setProperty('--background-color', c.backgroundColor);
    root.style.setProperty('--surface-color', c.surfaceColor);
    root.style.setProperty('--accent-color', c.accentColor);
    root.style.setProperty('--text-color', c.textColor);
    root.style.setProperty('--text-secondary-color', c.textSecondaryColor);
    root.style.setProperty('--border-color', c.borderColor);
    root.style.setProperty('--error-color', c.errorColor);
    root.style.setProperty('--success-color', c.successColor);
    root.style.setProperty('--on-secondary-color', c.onSecondaryColor);
    root.style.setProperty('--secondary-hover-color', c.secondaryHoverColor);
    root.style.setProperty('--transition-duration', c.transitionDuration);
    root.style.setProperty('--easing', c.easing);

    if (c.faviconUrl) {
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = c.faviconUrl;
    }
  }

  async function loadConfig() {
    if (isLoaded.value) return config.value;

    // Aplica defaults imediatamente para evitar flash
    applyConfig();

    try {
      // baseURL no api.ts é a ORIGIN (sem /api). Path completo aqui.
      const response = await api.get('/api/public/white-label/config');
      config.value = { ...DEFAULTS, ...response.data };
      applyConfig();
    } catch {
      // Falha silenciosa — continua com os defaults
    } finally {
      isLoaded.value = true;
    }

    return config.value;
  }

  return { isLoaded, config, loadConfig, applyConfig };
});
